import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Brain, 
  Target, 
  Crown, 
  Users, 
  Zap, 
  ChevronRight,
  BookOpen,
  Trophy,
  Briefcase,
  CheckCircle2,
  X,
  GraduationCap
} from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{
            backgroundImage: 'linear-gradient(rgba(249,115,22,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black"></div>
        
        <div className="relative z-10 container mx-auto px-6 text-center">
          {/* Scholar Athlete Logo */}
          <img 
            src="/images/scholar-athlete-logo-new.png" 
            alt="Scholar Athlete Academy" 
            className="mx-auto h-40 md:h-56 lg:h-64 w-auto mb-8 drop-shadow-2xl"
            style={{ filter: 'drop-shadow(0 0 30px rgba(249,115,22,0.4))' }}
          />
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight tracking-tight">
            WE DON'T BUILD<br/>
            <span className="text-orange-500">STUDENTS.</span><br/>
            WE BUILD <span className="text-orange-500">PROS.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            The world's first <span className="text-white font-bold">High-Performance Logic Engine</span> designed for the elite athlete.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => navigate('/academy')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-black text-lg px-8 py-6 uppercase tracking-wider"
              data-testid="claim-roster-btn"
            >
              Claim Your Roster Spot
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => navigate('/academy?type=parent')}
              variant="outline"
              className="border-2 border-gray-700 text-white hover:bg-gray-900 font-bold text-lg px-8 py-6"
            >
              Executive Mirror →
            </Button>
          </div>
        </div>
      </section>

      {/* The Problem vs The Solution */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              THE <span className="text-red-500">PROBLEM</span> VS THE <span className="text-green-500">SOLUTION</span>
            </h2>
            <p className="text-gray-500 text-lg">Why we built something different.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* The Problem */}
            <Card className="bg-red-500/5 border-2 border-red-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <X className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="text-2xl font-black text-red-500">THE OLD WAY</h3>
                </div>
                
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-1 shrink-0" />
                    <span className="text-gray-400">Generic tutoring platforms that treat athletes like every other student</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-1 shrink-0" />
                    <span className="text-gray-400">Abstract drills disconnected from real-world application</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-1 shrink-0" />
                    <span className="text-gray-400">One-size-fits-all curriculum that ignores athletic context</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-1 shrink-0" />
                    <span className="text-gray-400">Parents left out of the performance equation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-1 shrink-0" />
                    <span className="text-gray-400">No NIL preparation or brand building education</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            {/* The Solution */}
            <Card className="bg-green-500/5 border-2 border-green-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-black text-green-500">THE PRSVR WAY</h3>
                </div>
                
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 shrink-0" />
                    <span className="text-gray-300">Proprietary <strong className="text-white">Logic Engine</strong> built specifically for athlete cognition</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 shrink-0" />
                    <span className="text-gray-300">Sports-contextual scenarios that connect math to the game</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 shrink-0" />
                    <span className="text-gray-300">AI-powered <strong className="text-white">Asst. Coach</strong> using athletic metaphors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 shrink-0" />
                    <span className="text-gray-300"><strong className="text-white">Executive Mirror</strong> curriculum for high-performing parents</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 shrink-0" />
                    <span className="text-gray-300">Brand identity and NIL readiness built into the curriculum</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Three Pillars */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="bg-orange-500/20 text-orange-500 border border-orange-500/50 mb-4">
              THE FRAMEWORK
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              THE THREE <span className="text-orange-500">PILLARS</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Every elite athlete masters three domains. We train all of them.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Pillar 1: Intellect */}
            <Card className="bg-gradient-to-b from-orange-500/10 to-transparent border-2 border-orange-500/30 hover:border-orange-500 transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-orange-500/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Brain className="w-10 h-10 text-orange-500" />
                </div>
                <Badge className="bg-orange-500 text-black font-bold mb-4">PILLAR 01</Badge>
                <h3 className="text-3xl font-black text-white mb-2">INTELLECT</h3>
                <p className="text-orange-500 font-bold text-sm uppercase tracking-wider mb-4">The Logic Engine</p>
                <p className="text-gray-400 leading-relaxed">
                  Sports-contextual academic mastery. Math through yardage. Literacy through scouting reports. Every concept connected to the game.
                </p>
                <div className="mt-6 pt-6 border-t border-gray-800">
                  <p className="text-xs text-gray-600 uppercase tracking-wider">Modules Include</p>
                  <p className="text-sm text-gray-400 mt-2">Quantitative Combine • Tactical Literacy • Strategic Mind</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Pillar 2: Strategy */}
            <Card className="bg-gradient-to-b from-blue-500/10 to-transparent border-2 border-blue-500/30 hover:border-blue-500 transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-blue-500/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Target className="w-10 h-10 text-blue-500" />
                </div>
                <Badge className="bg-blue-500 text-white font-bold mb-4">PILLAR 02</Badge>
                <h3 className="text-3xl font-black text-white mb-2">STRATEGY</h3>
                <p className="text-blue-500 font-bold text-sm uppercase tracking-wider mb-4">30-Day Sprint</p>
                <p className="text-gray-400 leading-relaxed">
                  Intensive diagnostic and performance sprint. Establish your baseline across Academic, Tactical, and Brand pillars.
                </p>
                <div className="mt-6 pt-6 border-t border-gray-800">
                  <p className="text-xs text-gray-600 uppercase tracking-wider">Skills Developed</p>
                  <p className="text-sm text-gray-400 mt-2">Deductive Reasoning • Probability Analysis • Decision Trees</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Pillar 3: Identity */}
            <Card className="bg-gradient-to-b from-yellow-500/10 to-transparent border-2 border-yellow-500/30 hover:border-yellow-500 transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-yellow-500/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Crown className="w-10 h-10 text-yellow-500" />
                </div>
                <Badge className="bg-yellow-500 text-black font-bold mb-4">PILLAR 03</Badge>
                <h3 className="text-3xl font-black text-white mb-2">IDENTITY</h3>
                <p className="text-yellow-500 font-bold text-sm uppercase tracking-wider mb-4">The Legacy Portfolio</p>
                <p className="text-gray-400 leading-relaxed">
                  Brand building and NIL readiness. Your Three Pillars. Your Two-Way Player assessment. Define who you are before others do.
                </p>
                <div className="mt-6 pt-6 border-t border-gray-800">
                  <p className="text-xs text-gray-600 uppercase tracking-wider">Outcomes</p>
                  <p className="text-sm text-gray-400 mt-2">Brand Voice • NIL Strategy • Leadership Presence</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Executive Mirror Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6 max-w-5xl">
          <Card className="bg-gradient-to-br from-purple-500/10 to-indigo-500/5 border-2 border-purple-500/30 overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Left: Content */}
                <div className="p-10 md:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Briefcase className="w-7 h-7 text-purple-500" />
                    </div>
                    <div>
                      <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/50 mb-1">
                        SCHOLAR PARENT ACADEMY
                      </Badge>
                      <h3 className="text-2xl font-black text-white">The Executive Mirror</h3>
                    </div>
                  </div>
                  
                  <p className="text-xl text-gray-300 leading-relaxed mb-6">
                    Behind every elite athlete is a <span className="text-purple-400 font-bold">high-performing manager</span>.
                  </p>
                  
                  <p className="text-gray-400 leading-relaxed mb-8">
                    SPA provides parents with the Executive Mirror of our curriculum, optimizing professional performance alongside their athlete's growth. Same Logic Engine. Different fuel.
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-500" />
                      <span className="text-gray-300">ROI & Investment Analysis modules</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-500" />
                      <span className="text-gray-300">Professional time optimization</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-500" />
                      <span className="text-gray-300">Transparency Toggle: See your athlete's progress</span>
                    </li>
                  </ul>
                  
                  <Button 
                    onClick={() => navigate('/academy?type=parent')}
                    className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
                  >
                    Enter the Executive Mirror
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                
                {/* Right: Visual */}
                <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/10 flex items-center justify-center p-12 border-l border-purple-500/20">
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-6 border-2 border-purple-500/50">
                      <Users className="w-16 h-16 text-purple-500" />
                    </div>
                    <p className="text-purple-400 font-bold text-sm uppercase tracking-wider">The Whole Household</p>
                    <p className="text-white text-2xl font-black mt-2">Wins Together</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              SAA VS <span className="text-gray-500">TRADITIONAL</span>
            </h2>
            <p className="text-gray-500 text-lg">The difference is in the details.</p>
          </div>
          
          <div className="overflow-hidden rounded-xl border-2 border-gray-800">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-900">
                  <th className="p-4 text-left text-gray-500 font-bold uppercase tracking-wider text-sm">Feature</th>
                  <th className="p-4 text-center text-orange-500 font-bold uppercase tracking-wider text-sm">SAA</th>
                  <th className="p-4 text-center text-gray-600 font-bold uppercase tracking-wider text-sm">Traditional</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                <tr className="hover:bg-gray-900/50 transition-colors">
                  <td className="p-4 text-gray-300">Learning Context</td>
                  <td className="p-4 text-center">
                    <span className="text-orange-500 font-bold">Tactical Scouting</span>
                  </td>
                  <td className="p-4 text-center text-gray-600">Abstract Drills</td>
                </tr>
                <tr className="hover:bg-gray-900/50 transition-colors">
                  <td className="p-4 text-gray-300">Math Examples</td>
                  <td className="p-4 text-center">
                    <span className="text-orange-500 font-bold">NIL contracts, court geometry</span>
                  </td>
                  <td className="p-4 text-center text-gray-600">Generic word problems</td>
                </tr>
                <tr className="hover:bg-gray-900/50 transition-colors">
                  <td className="p-4 text-gray-300">Reading Material</td>
                  <td className="p-4 text-center">
                    <span className="text-orange-500 font-bold">Scouting reports, contracts</span>
                  </td>
                  <td className="p-4 text-center text-gray-600">Random passages</td>
                </tr>
                <tr className="hover:bg-gray-900/50 transition-colors">
                  <td className="p-4 text-gray-300">AI Coaching</td>
                  <td className="p-4 text-center">
                    <span className="text-orange-500 font-bold">Athletic metaphors</span>
                  </td>
                  <td className="p-4 text-center text-gray-600">Generic hints</td>
                </tr>
                <tr className="hover:bg-gray-900/50 transition-colors">
                  <td className="p-4 text-gray-300">Parent Involvement</td>
                  <td className="p-4 text-center">
                    <span className="text-orange-500 font-bold">Executive Mirror curriculum</span>
                  </td>
                  <td className="p-4 text-center text-gray-600">Progress reports only</td>
                </tr>
                <tr className="hover:bg-gray-900/50 transition-colors">
                  <td className="p-4 text-gray-300">Brand Building</td>
                  <td className="p-4 text-center">
                    <span className="text-orange-500 font-bold">Legacy Portfolio module</span>
                  </td>
                  <td className="p-4 text-center text-gray-600">N/A</td>
                </tr>
                <tr className="hover:bg-gray-900/50 transition-colors">
                  <td className="p-4 text-gray-300">Performance Metric</td>
                  <td className="p-4 text-center">
                    <span className="text-orange-500 font-bold">Academic Power Score</span>
                  </td>
                  <td className="p-4 text-center text-gray-600">Letter grades</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-b from-black to-orange-500/10">
        <div className="container mx-auto px-6 text-center max-w-4xl">
          <Trophy className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            READY TO <span className="text-orange-500">COMPETE</span>?
          </h2>
          <p className="text-xl text-gray-400 mb-10 leading-relaxed">
            The roster fills fast. Claim your spot and start building your Academic Power Score today.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => navigate('/academy')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-black text-lg px-10 py-6 uppercase tracking-wider"
              data-testid="final-claim-roster-btn"
            >
              <GraduationCap className="w-5 h-5 mr-2" />
              Athlete Lab
            </Button>
            <Button 
              onClick={() => navigate('/academy?type=parent')}
              className="bg-purple-500 hover:bg-purple-600 text-white font-black text-lg px-10 py-6 uppercase tracking-wider"
            >
              <Briefcase className="w-5 h-5 mr-2" />
              Executive Mirror
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
